import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {Alert, Box, Button, Container, Stack, Tooltip, Typography} from '@mui/material';
import {OTPSplitDigitInput} from '../../../components/forms/OTPSplitDigitInput';
import {mainLinearGradient} from '../../../theme/gradients';
import {AuthHeading, AuthLogo} from '../base';
import ClearIcon from '@mui/icons-material/Clear';
import "../../../App.css";
import { useNavigate } from 'react-router-dom';
/**
 * Renders the 2-Step Verification page JSX.
 * @param {AuthenticatePageProps} props - Props for the component.
 * @returns {JSX.Element} - The rendered component.
 */
export const AuthenticatePageJSXOnly = props => {
  const {
    code,
    factor,
    setCode,
    incorrect,
    maskedEmail,
    maskedPhone,
    errorMessage,
    handleAuthenticate,
    handleSendAuthCode,
    sendNewCodeActivated,
    disabled
  } = props;
  
  const navigate = useNavigate();
  const handleTryDifferntEmailBtn = () => {
    navigate('/user-login')
  }
  return (
    <Container maxWidth="sm">
      <Stack spacing={2} alignItems="start" justifyContent="center" sx={{height: '100vh'}}>
        <AuthLogo />
        <AuthHeading>2-Step Verification</AuthHeading>
        <Typography color="darkgray">
          {factor === 'email'
            ? "Please check your email box for a verification code to confirm your identity."
            : ''}
        </Typography>
        <ErrorMessagePart incorrect={incorrect} message={errorMessage} />
        <Box
          sx={{
            bgcolor: 'grayscale.light',
            border: '1px solid lightgray',
            borderRadius: 5,
            p: 3,
            boxSizing: 'border-box',
            width: 1,
            backgroundColor : "#EAF9F9"
          }}
        >
          <Stack>
            <Typography sx={{mb: 1,fontSize : "18px", fontWeight : 400,color : "#A1A0A0"}}>
              {factor === 'phone' ? 'Please check your phone for a verification code to confirm your identity. ' : ''}A
              code was sent to
              <span style={{color: 'var(--aimdFlowSecondaryDarkTextColor)', fontWeight: 600,fontSize : "18px", marginLeft : "8px"}}>
                {factor === 'email' ? maskedEmail : maskedPhone}
              </span>
            </Typography>
            <OTPSplitDigitInput code={code} setCode={setCode} />
            <Button
              onClick={handleAuthenticate}
              disabled={disabled}
              variant="contained"
              disableElevation
              size="large"
              sx={{
                mt: "20px",
                borderRadius: "40px", 
                height : "65px",
                fontWeight : 600,
                fontSize : "20px",
                background: 'linear-gradient(92deg, #72D8DA 2.62%, #35C6C9 92.09%)',
                boxShadow: `0px 49px 49px 0px rgba(0, 0, 0, 0.09), 0px 12px 27px 0px rgba(0, 0, 0, 0.10)` ,
                color : "#fff",
                "&:hover": {
                  background: 'linear-gradient(92deg, #72D8DA 2.62%, #35C6C9 92.09%)', 
                  borderColor: "rgb(141, 209, 210)", 
             
                },
                "&.Mui-disabled": {
                  background: 'linear-gradient(92deg, #72D8DA 2.62%, #35C6C9 92.09%)', 
                  color : "#fff",
                },
              }}
              endIcon={<ArrowForwardIcon />}
            >
              Verify My Email 
            </Button>
          </Stack>
        </Box>
        <Stack direction="row" justifyContent="space-between" sx={{color: 'darkgray', width: 1}}>
          <Tooltip
            title={
              !sendNewCodeActivated
                ? 'Please wait for one minute after requesting a new code before using this button again.'
                : ''
            }
          >
            <span>
              <Button onClick={handleSendAuthCode} 
              sx={{ 
                color : "var(--aimdFlowSecondaryDarkTextColor)" , 
               fontSize : {lg : "16px", md : "16px", sm : "14px", xs : "12px"},
                fontWeight : 400,
                '&.Mui-disabled': {
                  color: '#A1A0A0',
                },
                '&:hover': {
                  backgroundColor: 'transparent', 
                }
              }} 
              startIcon={<RestartAltIcon sx={{fontSize : {lg : "20px", md : "20px", sm : "18px", xs : "16px"},}}/> } 
               disabled={!sendNewCodeActivated}>
                Send a new code
              </Button>
            </span>
          </Tooltip>
          <Button sx={{
            color : "#A1A0A0",
            fontSize : {lg : "16px", md : "16px", sm : "14px", xs : "12px"},
            fontWeight : 400,               
            '&:hover': {
               backgroundColor: 'transparent', 
            }}}
            onClick={handleTryDifferntEmailBtn}
            >
            <ClearIcon sx={{ fontSize : {lg : "20px", md : "20px", sm : "18px", xs : "16px"} , marginRight: {lg : "8px", md : "8px", sm : "6px", xs : "4px"}}}/> Try a different email
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
};

/**
 * Renders an error message component.
 *
 * @param {Object} props - The props object.
 * @param {boolean} props.incorrect - Indicates if the error message should be displayed.
 * @param {string} props.message - The error message to be displayed.
 * @returns {JSX.Element} - The rendered error message component.
 *
 * @throws {TypeError} - If the props object is not provided.
 *
 * @example
 * // Renders an error message component with the provided message.
 * <ErrorMessagePart incorrect={true} message="Invalid input." />
 *
 * @example
 * // Renders an error message component with the default message.
 * <ErrorMessagePart incorrect={true} />
 */
const ErrorMessagePart = ({incorrect, message}) => {
  return incorrect ? (
    <Alert
      severity="error"
      sx={{
        boxSizing: 'border-box',
        width: '100%',
        marginX: 'auto',
      }}
    >
      {message ?? 'Sorry, please try again.'}
    </Alert>
  ) : (
    ''
  );
};
